import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './RandevuTakvimi.css';
import api from './api';

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function DiyetisyenDashboard({ theme }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [panoVerileri, setPanoVerileri] = useState(null);
  const [tumRandevular, setTumRandevular] = useState([]);
  const [seciliTarih, setSeciliTarih] = useState(new Date());
  const [seansUyarilari, setSeansUyarilari] = useState([]);
  const [profil, setProfil] = useState(null);

  const verileriGetir = useCallback(async () => {
    try {
      const [panoRes, randevuRes, seansRes, profilRes] = await Promise.all([
        api.get('/diyetisyen/pano'),
        api.get('/diyetisyen/randevular'),
        api.get('/diyetisyen/pano/suresi-dolan-seanslar'),
        api.get('/diyetisyen/profil')
      ]);

      setProfil(profilRes.data);
      setPanoVerileri(panoRes.data);
      setTumRandevular(randevuRes.data.map(event => ({
        ...event,
        start: new Date(event.tarih_saat),
      })));
      setSeansUyarilari(seansRes.data);

    } catch (err) {
      setError("Pano verileri yüklenirken bir hata oluştu.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verileriGetir();
  }, [verileriGetir]);

  const seciliGunRandevulari = useMemo(() => {
    return tumRandevular
      .filter(randevu => isSameDay(randevu.start, seciliTarih))
      .sort((a, b) => a.start - b.start);
  }, [tumRandevular, seciliTarih]);

  if (loading) return <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'dark'} />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const bugun = new Date();
  const saat = bugun.getHours();
  const selam = saat < 12 ? 'Günaydın' : (saat < 18 ? 'İyi günler' : 'İyi akşamlar');
  const bugunRandevulari = tumRandevular.filter(r => isSameDay(r.start, bugun)).sort((a, b) => a.start - b.start);
  const siradaki = bugunRandevulari.find(r => r.start >= new Date(bugun.getTime() - 30 * 60000)) || null;
  const ad = profil?.kullanici_adi || '';
  const RENKLER = [['#E7F5E2', '#3C8D4F'], ['#FFF0DE', '#E08A2E'], ['#EDE9FF', '#6B4EE6'], ['#E3F1FB', '#2B7BB9'], ['#FDE7EC', '#D2486A']];
  const seciliGunBugunMu = isSameDay(seciliTarih, bugun);

  return (
    <div className="bento-grid" style={{ gridTemplateRows: 'auto' }}>
      {/* KARŞILAMA */}
      <div className="bento-tile bento-hero" style={{ gridColumn: 'span 5', gridRow: 'span 2', minHeight: 250 }}>
        <div className="blob" />
        <div className="eyebrow">{bugun.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        <h1>{selam}{ad ? `, ${ad}` : ''} 👋<br />Bugün {panoVerileri?.ozet.bugunku_randevu_sayisi ?? 0} randevun var.</h1>
        <p className="muted mt-2 mb-0">
          {siradaki
            ? `Sıradaki randevu ${siradaki.start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}'da ${siradaki.danisan_ad_soyad} ile.`
            : 'Bugün için bekleyen randevu yok.'}
        </p>
        <div className="d-flex flex-wrap gap-2" style={{ marginTop: 28 }}>
          <Link to="/randevular" className="bento-pill-btn lime"><span className="material-symbols-outlined">add</span>Randevu</Link>
          <Link to="/danisan/yeni" className="bento-pill-btn ghost"><span className="material-symbols-outlined">person_add</span>Danışan</Link>
          <Link to="/sablonlar" className="bento-pill-btn ghost"><span className="material-symbols-outlined">note_stack</span>Şablonlar</Link>
          {siradaki && <Link to={`/randevu/${siradaki.id}`} className="bento-pill-btn ghost"><span className="material-symbols-outlined">play_arrow</span>Sıradakini aç</Link>}
        </div>
        {profil?.logo_data && (
          <div className="d-none d-md-flex align-items-center justify-content-center p-2 rounded-3 position-absolute"
            style={{ right: 22, bottom: 22, backgroundColor: '#ffffff', borderRadius: 14 }}
            title={profil.klinik_adi || profil.kullanici_adi}>
            <img src={profil.logo_data} alt="Klinik logosu" style={{ maxHeight: '44px', maxWidth: '130px', objectFit: 'contain' }} />
          </div>
        )}
      </div>

      {/* İSTATİSTİKLER */}
      {panoVerileri && (
        <>
          <div className="bento-tile bento-stat half-md" style={{ gridColumn: 'span 2' }}>
            <div className="ic" style={{ background: '#E7F5E2', color: '#3C8D4F' }}><span className="material-symbols-outlined">group</span></div>
            <div className="label">Aktif danışan</div>
            <div className="value">{panoVerileri.ozet.aktif_danisan_sayisi}</div>
          </div>
          <div className="bento-tile bento-stat half-md" style={{ gridColumn: 'span 2' }}>
            <div className="ic" style={{ background: '#FFF0DE', color: '#E08A2E' }}><span className="material-symbols-outlined">event_available</span></div>
            <div className="label">Bugünkü randevu</div>
            <div className="value">{panoVerileri.ozet.bugunku_randevu_sayisi}</div>
          </div>
          <div className="bento-tile bento-stat" style={{ gridColumn: 'span 3' }}>
            <div className="ic" style={{ background: '#EDE9FF', color: '#6B4EE6' }}><span className="material-symbols-outlined">date_range</span></div>
            <div className="label">Bu haftaki randevu</div>
            <div className="value">{panoVerileri.ozet.haftalik_randevu_sayisi}</div>
          </div>
        </>
      )}

      {/* AJANDA (takvimde seçili gün) */}
      <div className="bento-tile" style={{ gridColumn: 'span 4', gridRow: 'span 3' }}>
        <h3>
          {seciliGunBugunMu ? 'Bugünün ajandası' : `${seciliTarih.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} ajandası`}
          <small>{seciliGunRandevulari.length} randevu</small>
        </h3>
        {seciliGunRandevulari.length > 0 ? (
          seciliGunRandevulari.map((randevu, i) => {
            const [bg, fg] = RENKLER[i % RENKLER.length];
            const harfler = randevu.danisan_ad_soyad.split(' ').map(p => p.charAt(0)).join('').slice(0, 2).toUpperCase();
            return (
              <div key={randevu.id} className="bento-row">
                <div className="fw-bold" style={{ width: 48, fontSize: 14 }}>{randevu.start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</div>
                <div className="bento-avatar-sq" style={{ background: bg, color: fg }}>{harfler}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate" style={{ fontSize: 14 }}>{randevu.danisan_ad_soyad}</div>
                  <div className="text-truncate" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{randevu.konu || 'Randevu'}</div>
                </div>
                {siradaki && siradaki.id === randevu.id && <span className="bento-chip ms-auto" style={{ background: '#1F3D2B', color: '#C9F27B' }}>Sıradaki</span>}
                <Link to={`/randevu/${randevu.id}`} className={`bento-pill-btn soft ${siradaki && siradaki.id === randevu.id ? '' : 'ms-auto'}`} style={{ padding: '6px 14px', fontSize: 13 }}>Aç</Link>
              </div>
            );
          })
        ) : (
          <div className="bento-empty">
            <span className="material-symbols-outlined">event_busy</span>
            {seciliGunBugunMu ? 'Bugün için planlanmış randevu yok.' : 'Bu gün için planlanmış randevu yok.'}
          </div>
        )}
      </div>

      {/* SEANS UYARILARI */}
      <div className="bento-tile" style={{ gridColumn: 'span 3', gridRow: 'span 3', background: 'color-mix(in srgb, #EDE9FF 70%, var(--bg-secondary))' }}>
        <h3 style={{ color: '#1A1D1A' }}>Seans uyarıları <small>{seansUyarilari.length}</small></h3>
        {seansUyarilari.length > 0 ? (
          <div className="d-flex flex-column gap-2">
            {seansUyarilari.map(d => (
              <div key={d.id} className="d-flex align-items-center gap-2 p-2 rounded-4" style={{ background: 'rgba(255,255,255,.75)' }}>
                <span className="material-symbols-outlined" style={{ color: d.durum === 'bitmis' ? '#D2486A' : '#6B4EE6' }}>{d.durum === 'bitmis' ? 'error' : 'hourglass_bottom'}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate" style={{ fontSize: 14, color: '#1A1D1A' }}>{d.ad_soyad}</div>
                  <div className="text-truncate" style={{ fontSize: 12, color: '#6B706B' }}>{d.paket_adi} • {d.kullanilan_seans}/{d.toplam_seans} seans</div>
                </div>
                <span className="bento-chip ms-auto" style={d.durum === 'bitmis' ? { background: '#FDE7EC', color: '#B3264A' } : { background: '#FFC34D', color: '#5A3D00' }}>
                  {d.durum === 'bitmis' ? 'Seans bitti' : `${d.kalan_seans} seans kaldı`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="bento-empty" style={{ color: '#6B706B' }}><span className="material-symbols-outlined">verified</span>Paketi bitmek üzere olan danışan yok.</div>
        )}
      </div>

      {/* TAKVİM */}
      <div className="bento-tile" style={{ gridColumn: 'span 5' }}>
        <h3>Takvim <small>randevulu günler işaretli</small></h3>
        <Calendar
          onChange={setSeciliTarih}
          value={seciliTarih}
          locale="tr-TR"
          minDetail="month"
          tileClassName={({ date, view }) =>
            view === 'month' && tumRandevular.some(r => isSameDay(r.start, date)) ? 'randevu-var' : null
          }
        />
      </div>
    </div>
  );
}

export default DiyetisyenDashboard;